import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getERPClient } from '../../services/supplyChainApi';

const getItemId = (item) => item?.id || item?.item_id || item?.value || '';
const getItemName = (item) => item?.name_en || item?.name_ar || item?.name || '';
const getItemLabel = (item) =>
  `${item?.item_code || ''}${item?.item_code && getItemName(item) ? ' - ' : ''}${getItemName(item) || ''}`;

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function ERPItemSearchField({
  name,
  label,
  items = [],
  isLoading,
  onSelect,
  fallbackLabel,
  disabled = false,
  width = 360,
}) {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [serverResults, setServerResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 350);

  // Close on outside mousedown (but not clicks inside the popup), window scroll,
  // or resize while open.
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onReposition = () => {
      if (triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, width) });
      }
    };
    window.addEventListener('mousedown', onOutside);
    document.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('mousedown', onOutside);
      document.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, width]);

  useEffect(() => {
    if (!open || !debouncedQuery.trim()) {
      setServerResults(null);
      return;
    }
    let cancelled = false;
    setIsSearching(true);
    (async () => {
      try {
        const erpClient = getERPClient();
        const res = await erpClient.getItems({ q: debouncedQuery.trim(), limit: 20, offset: 0, is_active: true });
        const list = Array.isArray(res) ? res : (res?.data || []);
        if (!cancelled) setServerResults(list);
      } catch (err) {
        console.error('Item search failed:', err);
        if (!cancelled) setServerResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open]);

  const filteredItems = useMemo(() => {
    if (debouncedQuery.trim()) return serverResults || [];
    // Server-side search: don't offer the full list until the user types.
    return [];
  }, [debouncedQuery, serverResults]);

  const allItems = Array.isArray(items) ? items : [];
  const selectedItem = allItems.find((item) => getItemId(item) === name);
  const selectedLabel = selectedItem
    ? getItemLabel(selectedItem)
    : selectedResult && getItemId(selectedResult) === name
      ? getItemLabel(selectedResult)
      : fallbackLabel || '';

  const openPopup = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, width) });
    setQuery('');
    setServerResults(null);
    setSelectedResult(null);
    setOpen(true);
  };

  return (
    <>
      <div className="min-w-0">
        {label && <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>}
        <button
          type="button"
          ref={triggerRef}
          disabled={disabled}
          onClick={openPopup}
          className="flex h-[30px] w-full items-center justify-between bg-gray-100 px-2 text-xs text-gray-700 border border-gray-300 rounded disabled:opacity-60"
        >
          <span className="truncate text-start">{selectedLabel || t('Search and select item')}</span>
          <i className="pi pi-chevron-down text-[10px] opacity-60" />
        </button>
      </div>

      {open &&
        rect &&
        createPortal(
          <div
            ref={popupRef}
            className="fixed z-[2000] rounded border border-gray-300 bg-white p-2 shadow-xl"
            style={{ top: rect.top, left: rect.left, width: rect.width }}
          >
            <div className="relative">
              <i className="pi pi-search absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Search items')}
                className="w-full border border-gray-300 rounded pl-7 pr-7 py-1 text-xs focus:outline-none focus:border-primary"
              />
              {query && !isSearching && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <i className="pi pi-times text-[10px]" />
                </button>
              )}
              {isSearching && (
                <i className="pi pi-spin pi-spinner absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-primary" />
              )}
            </div>

            <div className="max-h-72 mt-1 space-y-1 overflow-y-auto">
              {isSearching ? (
                <div className="px-2 py-4 text-center text-xs text-gray-500">{t('Loading...')}</div>
              ) : !debouncedQuery.trim() ? (
                <div className="px-2 py-4 text-center text-xs text-gray-500">{t('Type to search...')}</div>
              ) : isLoading ? (
                <div className="px-2 py-4 text-center text-xs text-gray-500">{t('Loading...')}</div>
              ) : filteredItems.length ? (
                filteredItems.map((item) => {
                  const itemId = getItemId(item);
                  return (
                    <button
                      key={itemId}
                      type="button"
                      onClick={() => {
                        setSelectedResult(item);
                        onSelect?.(item);
                        setQuery('');
                        setServerResults(null);
                        setOpen(false);
                      }}
                      className="flex w-full items-start justify-between rounded px-2 py-2 text-start hover:bg-blue-50"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-gray-800">{getItemLabel(item)}</div>
                        <div className="truncate text-[11px] text-gray-500">
                          {item.trade_name_en || item.description_en || t('Item')}
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-2 py-4 text-center text-xs text-gray-500">{t('No matching items found')}</div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}